import useDarkMode, { Theme } from '@hooks/useDarkMode'
import './index.scss'

import logo from 'url:@assets/images/HeaderLogo.png'
import logoDark from 'url:@assets/images/HeaderLogo-dark.png'

import icon from 'url:@assets/icons/house.svg'
import servicePlansIcon from 'url:@assets/icons/persontime.svg'
import connectionsIcon from 'url:@assets/icons/connections.svg'
import playlistsIcon from 'url:@assets/icons/music.list.svg'
import rightArrowIcon from 'url:@assets/icons/arrow.right.svg'
import teamIcon from 'url:@icon/person.svg'
import eventsIcon from 'url:@assets/icons/events-icon.svg'

import sidebarIcon from 'url:@icon/sidebar.svg'

import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@auth/AuthProvider'

import { slide as Menu } from 'react-burger-menu'
import { useState } from 'react'
import { StaffAction } from '@data/interfaces/church.interfaces'

type Route = {
  path: string;
  name: string;
  icon: string;
  requiredPermission: StaffAction | null;
}

type Separator = {
  type: 'separator';
  label?: string;
  requiredPermission?: StaffAction | null;
}

type SidebarItem = Route | Separator;

const routes: SidebarItem[] = [
  {
    path: 'home',
    name: 'Home',
    icon: icon,
    requiredPermission: null
  },
  {
    type: 'separator',
    label: 'Content'
  },
  // this is disabled until we decide it's a useful feature & we build out the removed screens
  // {
  //   path: 'service-plans',
  //   name: 'Service Plans',
  //   icon: servicePlansIcon,
  //   requiredPermission: StaffAction.CREATE_MINISTRY_DATA
  // },
  {
    path: 'playlists',
    name: 'Playlists',
    icon: playlistsIcon,
    requiredPermission: StaffAction.CREATE_PLAYLIST
  },
  {
    path: 'events',
    name: 'Events',
    icon: eventsIcon,
    requiredPermission: StaffAction.CREATE_MINISTRY_DATA // TODO: this will need to be tweaked at some point
  },
  // Add a separator here to group integrations / admin links
  {
    type: 'separator',
    label: 'Settings'
  },
  {
    path: 'team',
    name: 'Team Members',
    icon: teamIcon,
    requiredPermission: StaffAction.EDIT_STAFF_USER
  },
  {
    path: 'connections',
    name: 'Connections',
    icon: connectionsIcon,
    requiredPermission: StaffAction.EDIT_CHURCH_METADATA
  }
]

export default function Sidebar(props: {
  churchName: string;
  isMobile: boolean
}) {
  const [isOpen, setIsOpen] = useState(false);

  const sidebarContent = <SidebarContent churchName={props.churchName} closeSidebar={() => {
    setIsOpen(false)
  }} />

  return props.isMobile ? <>
    <header className='sidebar-header'>
      <a onClick={(e) => {
        e.preventDefault();

        setIsOpen(true)
      }}>
        <img src={sidebarIcon} />
      </a>
      <h1>Salt Church Hub</h1>
    </header>

    <Menu isOpen={isOpen} onStateChange={(state) => setIsOpen(state.isOpen)} className='sidebar' styles={{
      bmBurgerButton: {
        display: 'none'
      }
    }}>
      {sidebarContent}
    </Menu>
  </> : <div className='sidebar sidebar-div'>
    {sidebarContent}
  </div>
}

function SidebarContent(props: {
  churchName: string,
  closeSidebar: () => void
}) {
  const theme = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const path = location.pathname;
  let pathComponents = path.split('/');
  let route = pathComponents[1];

  if(route === "") {
    route = "home"
  }

  return <>
    <img src={theme === Theme.LIGHT ? logo : logoDark}
      draggable={false}
      alt="Salt Icon"
      className='logo' />

    <p className='greeting'>👋 Hi, {auth.user?.name}!</p>
    <p className='churchName'>{props.churchName}</p>

    <div className='divider' />

    <ul>
      {routes.map((r, i) => {
        // handle separator items
        if ((r as any).type === 'separator') {
          const sep = r as Separator;

          if (sep.requiredPermission && !auth.hasPermission(sep.requiredPermission)) {
            return null;
          }

          return <li key={`sep-${i}`} className='route-separator'>
            {sep.label ? <p className='separator-label'>{sep.label}</p> : <div className='separator-line' />}
          </li>
        }

        const routeItem = r as Route;
        if (routeItem.requiredPermission && !auth.hasPermission(routeItem.requiredPermission)) {
          return null;
        }

        return <li key={i} className={route == routeItem.path ? 'selected' : ''}>
        <a href={`/${routeItem.path}`} onClick={(e) => {
          e.preventDefault();

          navigate(`/${routeItem.path}`);
          props.closeSidebar();
        }}>
          <img src={routeItem.icon} />
          <p>{routeItem.name}</p>
        </a>
      </li>
      })}
    </ul>

    <div className='divider' />

    <ul>
      <li>
        <a href="/logout" onClick={(e) => {
          e.preventDefault();

          auth.signOut(() => navigate("/"))
          props.closeSidebar();
        }}>
          <p style={{ marginRight: 8 }}>Sign Out</p>
          <img src={rightArrowIcon} />
        </a>
      </li>
    </ul>
  </>
}